import { useQuery, keepPreviousData } from "@tanstack/react-query";

import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { readComplianceBody } from "../helper/readComplianceBody";

type StoredToken = { AccessToken?: string };

export type FlaggedUsersSearchBy = "user_id" | "email" | "phone";

export type FlaggedUsersFilters = {
  page?: number;
  page_size?: number;
  page_token?: string | null;
  search_by?: FlaggedUsersSearchBy;
  search_value?: string;
  start_date?: string;
  end_date?: string;
};

export type FlaggedUserApiItem = {
  flagged_at: string;
  user_id: string;
  username: string;
  rule_name_triggered: string;
  alert_id: string;
  /**
   * Seconds elapsed between the rule's evaluation window start and the moment
   * the user crossed the threshold (surfaced to the UI as "Time to Trigger
   * Flag"). Always provided in seconds by the backend; the UI converts it
   * into the rule's own period unit for display.
   */
  time_to_alert_seconds?: number | null;
};

export type FlaggedUsersListApiResponse = {
  items: FlaggedUserApiItem[];
  page: number;
  page_size: number;
  total: number | null;
  next_page_token: string | null;
};

// Kept in sync with FlaggedUserRow in FlaggedUsersTable.tsx.
export type FlaggedUserData = {
  id: string;
  flagged_at: string | null;
  user_id: string;
  username: string;
  rule_name: string;
  /**
   * Raw seconds from the backend ("Time to Trigger Flag"). Formatting happens
   * at render time so the value can be expressed in the unit of the rule that
   * actually triggered it.
   */
  time_to_alert_seconds: number | null;
};

export type FlaggedUsersResponse = {
  data: FlaggedUserData[];
  total: number;
  page: number;
  page_size: number;
  next_page_token: string | null;
};

function mapItemToRow(item: FlaggedUserApiItem): FlaggedUserData {
  const rawSeconds = item.time_to_alert_seconds;
  const seconds =
    typeof rawSeconds === "number" && Number.isFinite(rawSeconds)
      ? rawSeconds
      : null;
  return {
    id: item.alert_id,
    flagged_at: item.flagged_at,
    user_id: item.user_id,
    username: item.username,
    rule_name: item.rule_name_triggered,
    time_to_alert_seconds: seconds,
  };
}

function resolveTotal(raw: FlaggedUsersListApiResponse): number {
  if (raw.total != null) return raw.total;
  const page = raw.page ?? 1;
  const pageSize = raw.page_size ?? 20;
  const n = raw.items.length;
  // When total is unknown, avoid page * pageSize + 1 (ignores current page row count and
  // makes the table total jump on partial pages). Bound: rows seen + optional +1 if more pages.
  return (page - 1) * pageSize + n + (raw.next_page_token ? 1 : 0);
}

const fetchFlaggedUsers = async (filters: FlaggedUsersFilters): Promise<FlaggedUsersResponse> => {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString ? JSON.parse(tokenString) : null;
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(token?.AccessToken);

  const page = filters.page ?? 1;
  const page_size = filters.page_size ?? 20;

  const body: Record<string, unknown> = {
    page,
    page_size,
  };
  if (filters.page_token != null && filters.page_token !== "") {
    body.page_token = filters.page_token;
  }
  if (filters.start_date) body.start_date = filters.start_date;
  if (filters.end_date) body.end_date = filters.end_date;
  const q = filters.search_value?.trim();
  if (q && filters.search_by) {
    body.q = q;
    body.search_mode = filters.search_by;
  }

  const response = await PrivateAdminPanelInstance.post(
    API_ROUTES.POST_FRAUD_FLAGGED_USERS_LIST,
    body,
  );

  const raw = readComplianceBody<FlaggedUsersListApiResponse>(response) ?? {
    items: [],
    page,
    page_size,
    total: 0,
    next_page_token: null,
  };

  const items = Array.isArray(raw.items) ? raw.items : [];
  const data = items.map(mapItemToRow);

  return {
    data,
    total: resolveTotal({
      ...raw,
      items,
      page: raw.page ?? page,
      page_size: raw.page_size ?? page_size,
    }),
    page: raw.page ?? page,
    page_size: raw.page_size ?? page_size,
    next_page_token: raw.next_page_token ?? null,
  };
};

export const useGetFlaggedUsers = (filters: FlaggedUsersFilters = {}) => {
  return useQuery({
    queryKey: ["GET_FRAUD_FLAGGED_USERS_LIST", filters],
    queryFn: () => fetchFlaggedUsers(filters),
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
};
